import gql from "graphql-tag";
import { auditTableConfigFragment } from "./fragments";

/**
 * Audit configuration for the Orcha database.
 *
 * Fides federates the Orcha GraphQL service, so this query is
 * routed to the Orcha subgraph by the Apollo gateway.
 */
export const GET_ORCHA_AUDIT_TABLE_CONFIGS = gql`
  query getOrchaAuditTableConfigs {
    auditTableConfigs {
      ...AuditTableConfigFragment
    }
  }
  ${auditTableConfigFragment}
`;

/**
 * Audit configuration for the Fides database.
 *
 * Served by the Fides subgraph. Until the Fides audit migrations
 * are ported from Orcha, this query returns an empty list.
 */
export const GET_FIDES_AUDIT_TABLE_CONFIGS = gql`
  query getFidesAuditTableConfigs {
    fidesAuditTableConfigs {
      ...AuditTableConfigFragment
    }
  }
  ${auditTableConfigFragment}
`;

/**
 * Generic audit history query that retrieves audit logs for any table/record.
 *
 * This query traverses the audit log to find:
 * - The record itself (matching record_key.id = recordId)
 * - Direct children (rows where foreign_key or changed_foreign_key references this record)
 *
 * @param tableName - The name of the table (e.g., "activities", "tasks")
 * @param recordId - The UUID of the record
 * @param depth - Maximum traversal depth (default 0)
 */
export const GET_AUDIT_HISTORY = gql`
  query GetAuditHistory($tableName: String!, $recordId: ID!, $depth: Int) {
    auditHistory(tableName: $tableName, recordId: $recordId, depth: $depth) {
      id
      schemaName
      tableName
      recordKey
      foreignKey
      changedForeignKey
      operation
      oldData
      newData
      actorUserId
      actionName
      transactionId
      createdAt
    }
  }
`;

/**
 * Normalized history events query that returns structured history events.
 *
 * This query returns:
 * - Grouped base/subtype rows into logical events
 * - Semantic operation (create/update/delete) from actionName
 * - Structured field changes for updates
 * - Human-readable entity type and label
 *
 * @param tableName - The name of the table (e.g., "activities", "tasks")
 * @param recordId - The UUID of the record
 * @param depth - Maximum traversal depth (default 5)
 */
export const GET_HISTORY_EVENTS = gql`
  query GetHistoryEvents($tableName: String!, $recordId: ID!, $depth: Int) {
    historyEvents(tableName: $tableName, recordId: $recordId, depth: $depth) {
      id
      transactionId
      createdAt
      actor {
        id
        name
      }
      events {
        id
        createdAt
        operation
        entity {
          id
          type
          label
        }
        changes {
          field
          oldValue {
            raw
            display
          }
          newValue {
            raw
            display
          }
          type
        }
      }
    }
  }
`;
